import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Edit, Search, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountsCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const fetch = useCallback(async () => {
    const r = await axios.get(`${API}/accounts/customers`);
    setCustomers(r.data);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/accounts/customers/${editing.id}`, form);
      toast.success("Customer updated");
      setShowEdit(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      gst_number: c.gst_number || "",
      address: c.address || "",
      city: c.city || "",
      state: c.state || "",
      pin_code: c.pin_code || "",
      phone: c.phone || "",
      email: c.email || "",
      pan_number: c.pan_number || "",
      contact_person: c.contact_person || "",
    });
    setShowEdit(true);
  };

  const filtered = customers.filter(c => {
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.gst_number?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="accounts-customers-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><Users className="w-5 h-5" /> Customer Database</h2>
          <p className="text-gray-400 text-sm">{customers.length} customers</p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, GST, city..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9" />
      </div>

      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400 w-8"></TableHead>
                <TableHead className="text-gray-400">Name</TableHead>
                <TableHead className="text-gray-400">GST Number</TableHead>
                <TableHead className="text-gray-400">City</TableHead>
                <TableHead className="text-gray-400">State</TableHead>
                <TableHead className="text-gray-400">Phone</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No customers found.</TableCell></TableRow>
              ) : filtered.map(c => (
                <React.Fragment key={c.id}>
                  <TableRow className="border-gray-800 hover:bg-gray-800/40 cursor-pointer" onClick={() => setExpanded(p => ({...p, [c.id]: !p[c.id]}))}>
                    <TableCell>{expanded[c.id] ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}</TableCell>
                    <TableCell className="text-white font-medium">{c.name}</TableCell>
                    <TableCell className="text-cyan-400 font-mono text-xs">{c.gst_number || "—"}</TableCell>
                    <TableCell className="text-gray-300">{c.city || "—"}</TableCell>
                    <TableCell className="text-gray-300">{c.state || "—"}</TableCell>
                    <TableCell className="text-gray-300">{c.phone || "—"}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => openEdit(c)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded[c.id] && (
                    <TableRow className="border-gray-800 bg-gray-900/50">
                      <TableCell colSpan={7} className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-gray-500">Address:</span> <span className="text-gray-300 ml-1">{c.address || "—"}</span></div>
                          <div><span className="text-gray-500">PIN:</span> <span className="text-gray-300 ml-1">{c.pin_code || "—"}</span></div>
                          <div><span className="text-gray-500">Email:</span> <span className="text-gray-300 ml-1">{c.email || "—"}</span></div>
                          <div><span className="text-gray-500">PAN:</span> <span className="text-gray-300 ml-1">{c.pan_number || "—"}</span></div>
                          <div><span className="text-gray-500">Contact:</span> <span className="text-gray-300 ml-1">{c.contact_person || "—"}</span></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-blue-400">Edit Customer Details</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div><Label className="text-gray-300">Company Name</Label><Input value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">GST Number</Label><Input value={form.gst_number || ""} onChange={e => setForm({...form, gst_number: e.target.value})} placeholder="e.g., 27AABCT1234A1Z5" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">PAN Number</Label><Input value={form.pan_number || ""} onChange={e => setForm({...form, pan_number: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Address</Label><Input value={form.address || ""} onChange={e => setForm({...form, address: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-gray-300">City</Label><Input value={form.city || ""} onChange={e => setForm({...form, city: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">State</Label><Input value={form.state || ""} onChange={e => setForm({...form, state: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">PIN Code</Label><Input value={form.pin_code || ""} onChange={e => setForm({...form, pin_code: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300">Phone</Label><Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Email</Label><Input value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>
            <div><Label className="text-gray-300">Contact Person</Label><Input value={form.contact_person || ""} onChange={e => setForm({...form, contact_person: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsCustomers;
